import React from 'react';
import { UI } from '@gik/ui';
import { LoginFormSection } from '../components/Login';
import ModalButtonFooter from '@gik/ui/Modal/ModalButtonFooter';
import { IUserLoginFormResponse, IUserSignUpResponse } from '@gik/api/users/auth';
import { navigateTo } from '@/utils/environment';
import { ForgotPasswordSection } from '../components/ForgotPassword';
import { SignupModalContent } from '../components/Signup';
import { auth } from '..';
import bemBlock from '@gik/core/utils/bemBlock';
import SignupCalloutUpdates from '@gik/auth/components/Signup/SignupCalloutUpdates';
import SignupCalloutCalendar from '@gik/auth/components/Signup/SignupCalloutCalendar';
import { AuthModalSettings, CalloutBlockType } from '@gik/auth/utils/auth';

type LoginModalResponseType = IUserLoginFormResponse | 'forgot-password' | 'sign-up';

export async function openLoginModal(settings?: AuthModalSettings): Promise<IUserLoginFormResponse> {
  const footerId = 'LoginFormFooter';

  const response: LoginModalResponseType = await UI.dialog<LoginModalResponseType>(
    ({ close }) => {
      return (
        <LoginFormSection
          oauthErrorMessage={settings?.oauthErrorMessage}
          onSuccess={doneResponse => {
            close(doneResponse);
          }}
          onForgotPassword={() => {
            close('forgot-password');
            // timeout needed due to back button override changing history
            setTimeout(() => auth.resetPassword(), 500);
          }}
          onSignup={() => {
            close('sign-up');
            // timeout needed due to back button override changing history
            setTimeout(() => auth.signup(settings), 500);
          }}
          buttonsPortal={() => document.getElementById(footerId)}
        />
      );
    },
    {
      title: 'Login',
      closable: true,
      className: 'gik-auth-modal gik-login-modal',
      footer: <ModalButtonFooter id={footerId} />,
    }
  );

  if (response) {
    if (response === 'forgot-password' || response === 'sign-up') {
      // TODO: optimize conditional
    } else if (typeof response == 'object') {
      if (response.returnUrl) {
        navigateTo(response.returnUrl);
      }
      return response;
    }
  } else {
    await settings?.onCancel?.();
  }
}

export async function openForgotPasswordModal(): Promise<void> {
  const footerId = 'ForgotPasswordFormFooter';

  UI.dialog(
    ({ close }) => {
      return (
        <ForgotPasswordSection
          onSuccess={() => {
            close();
          }}
          buttonsPortal={() => document.getElementById(footerId)}
        />
      );
    },
    {
      title: 'Reset Password',
      closable: true,
      className: 'gik-auth-modal gik-forgot-password-modal',
      footer: <ModalButtonFooter id={footerId} />,
    }
  );
}

type SignupModalResponseType = IUserSignUpResponse | 'login';

/**
 * returns the new user's id on success, otherwise null
 */
export async function openSignupModal(settings?: AuthModalSettings): Promise<IUserSignUpResponse> {
  const footerId = 'SignupModalFormFooter';
  const blockName = 'signup-modal';
  const bem = bemBlock(blockName);

  let _calloutBlock;
  switch (settings?.calloutBlock) {
    case CalloutBlockType.UPDATES:
      _calloutBlock = <SignupCalloutUpdates recipientName={settings.recipientName} />;
      break;
    case CalloutBlockType.CALENDAR:
      _calloutBlock = <SignupCalloutCalendar recipientName={settings.recipientName} />;
      break;
    default:
      _calloutBlock = undefined;
  }

  const response: SignupModalResponseType = await UI.dialog<SignupModalResponseType>(
    ({ close }) => {
      return (
        <SignupModalContent
          onSuccess={response => {
            close(response);
          }}
          onLogin={() => {
            close('login');
            // timeout needed due to back button override changing history
            setTimeout(() => auth.login(settings), 500);
          }}
          buttonsPortal={() => document.getElementById(footerId)}
          calloutBlock={_calloutBlock}
        />
      );
    },
    {
      padding: false,
      closable: true,
      autowidth: false,
      className: bem(null, [{ [settings?.calloutBlock || 'generic']: true }]),
      footer: <ModalButtonFooter id={footerId} />,
    }
  );

  if (response) {
    if (response === 'login') {
      // TODO: optimize conditional
    } else if (typeof response == 'object') {
      if (response.returnUrl) {
        navigateTo(response.returnUrl);
      }
      return response;
    }
  } else {
    await settings?.onCancel?.();
  }
}
